/**
 * <ArcWakeWordListener /> — global "Hey Arc" trigger.
 *
 * Mounted once in `MainLayout`. Reads `arc.wakeWord.enabled` from
 * localStorage. When enabled, runs `useWakeWord` continuously. On a
 * detected wake phrase the user is navigated to `/arc?seed=<rest>` —
 * Arc Console already honours the `seed` query param.
 *
 * Why a separate component (not just baked into ArcConsolePage):
 *   - JARVIS-parity expectation is "I can say 'Hey Arc' from any page".
 *     Mounting here makes the wake phrase work even when the user is on
 *     /brain/causal or /assets — not only inside Arc itself.
 *   - Keeps wake-word lifecycle decoupled from Arc page mount/unmount,
 *     so toggling routes doesn't restart the recogniser.
 *
 * Why off by default:
 *   - Continuous mic listening is high-trust. Users must opt in via the
 *     toggle in Arc Console (which writes to the same localStorage key).
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDeploymentMode } from '@/hooks/useDeploymentMode'
import useWakeWord from '@/hooks/useWakeWord'
import { parseNavIntent } from '@/utils/voiceNavigation'

const ENABLED_KEY = 'arc.wakeWord.enabled'

export default function ArcWakeWordListener() {
  const navigate = useNavigate()
  const { isHub } = useDeploymentMode()
  // Spoke renders Arc as `/playground/agi` (Hub renders the full /arc
  // console). The wake target follows that split so the spoken handoff
  // lands on the same page the user can otherwise reach via sidebar.
  const arcPath = isHub ? '/arc' : '/playground/agi'

  const [enabled, setEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(ENABLED_KEY) === '1'
  })

  // Cross-tab + cross-component sync — Arc Console flips the same flag
  // and we want this listener to react without a page refresh.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onStorage = (e: StorageEvent) => {
      if (e.key === ENABLED_KEY) {
        setEnabled(e.newValue === '1')
      }
    }
    const onCustom = () => {
      setEnabled(window.localStorage.getItem(ENABLED_KEY) === '1')
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener('arc.wakeWord.changed', onCustom)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('arc.wakeWord.changed', onCustom)
    }
  }, [])

  const handleWake = useCallback(
    (rest: string) => {
      const trimmed = rest.trim()
      // First try to interpret the rest of the utterance as a navigation
      // intent: "Hey Arc, go to causal graph" → /brain/causal directly.
      // Only navigate without an Arc run if the parser is confident
      // (≥0.85) — lower confidence falls through to a regular Arc run
      // so we don't misroute on ambiguous prompts.
      if (trimmed.length > 0) {
        const intent = parseNavIntent(trimmed)
        if (intent && intent.confidence >= 0.85) {
          navigate(intent.path)
          return
        }
        navigate(`${arcPath}?seed=${encodeURIComponent(trimmed)}&wake=1`)
        return
      }
      navigate(`${arcPath}?wake=1`)
    },
    [navigate, arcPath],
  )

  // useWakeWord is a no-op when enabled=false, so this is cheap to mount.
  useWakeWord({
    enabled,
    onWake: handleWake,
  })

  return null
}
